<template>
  <div>
    <!-- Розділювач -->
    <div class="h-px w-full bg-gray-200"></div>

    <div class="text-gray-900 px-8 py-16">
      <div class="max-w-7xl mx-auto flex flex-col gap-12">
        <!-- Верхня частина -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-12 text-sm">
          <!-- Логотип та опис -->
          <div
            class="col-span-4 sm:col-span-2 lg:col-span-1 flex flex-col gap-6"
          >
            <div class="flex items-center gap-2 text-lg font-semibold">
              <BaseLogo />
            </div>
            <p class="text-gray-600 leading-relaxed">
              {{ $t('footer.description') }}
            </p>
            <span class="text-xs text-gray-500">
              {{
                $t('footer.allRightsReserved', {
                  year: new Date().getFullYear()
                })
              }}
            </span>
          </div>

          <!-- Правова інформація -->
          <div
            class="col-span-4 sm:col-span-2 lg:col-span-1 flex flex-col gap-3"
          >
            <span
              class="uppercase text-gray-600 font-semibold tracking-wide text-xs mb-2 block"
              >{{ $t('footer.legalInfo') }}</span
            >
            <router-link
              :to="{ name: 'public.terms-of-services' }"
              class="text-gray-700 hover:text-gray-900 hover:underline transition-colors duration-200"
            >
              {{ $t('footer.termsOfUse') }}
            </router-link>

            <router-link
              :to="{ name: 'public.privacy-policy' }"
              class="text-gray-700 hover:text-gray-900 hover:underline transition-colors duration-200"
            >
              {{ $t('footer.privacyPolicy') }}
            </router-link>
          </div>

          <!-- Посилання -->
          <div
            class="col-span-4 sm:col-span-2 lg:col-span-1 flex flex-col gap-3"
          >
            <span
              class="uppercase text-gray-600 font-semibold tracking-wide text-xs mb-2 block"
              >{{ $t('footer.linksSection') }}</span
            >

            <template v-if="isLanding">
              <span
                class="text-gray-700 hover:text-gray-900 hover:underline cursor-pointer transition-colors duration-200"
                @click="() => $emit('scroll-to-section', 'demo')"
              >
                {{ $t('landing.footerLinks.demo') }}
              </span>

              <span
                class="text-gray-700 hover:text-gray-900 hover:underline cursor-pointer transition-colors duration-200"
                @click="() => $emit('scroll-to-section', 'faq')"
              >
                {{ $t('landing.footerLinks.faq') }}
              </span>

              <span
                class="text-gray-700 hover:text-gray-900 hover:underline cursor-pointer transition-colors duration-200"
                @click="() => $emit('scroll-to-section', 'pricing')"
              >
                {{ $t('landing.footerLinks.pricing') }}
              </span>
            </template>

            <!-- <router-link
              :to="{ name: 'tutorial' }"
              class="text-gray-700 hover:text-gray-900 hover:underline transition-colors duration-200"
              >{{ $t('footer.tutorial') }}</router-link
            > -->
            <!-- <router-link
              :to="{
                name: 'hub.exercises',
                params: {
                  sectionSlug: 'present-tense',
                  topicSlug: 'present-simple'
                },
                query: {
                  isDemo: !user?.isPremium
                }
              }"
              class="text-gray-700 hover:text-gray-900 hover:underline transition-colors duration-200"
              >{{ $t('footer.presentSimpleTest') }}</router-link
            > -->
            <!-- <div
              v-if="!user?.isPremium"
              class="text-gray-700 hover:text-gray-900 hover:underline cursor-pointer transition-colors duration-200"
              @click="() => setUiPaywallModalIsVisible(true)"
            >
              {{ $t('footer.pricing') }}
            </div> -->
            <a
              href="https://www.flaticon.com/uicons"
              class="text-gray-700 hover:text-gray-900 hover:underline transition-colors duration-200"
              target="_blank"
            >
              {{ $t('footer.links.icons') }}
            </a>
            <a
              href="https://marclou.com/"
              class="text-gray-700 hover:text-gray-900 hover:underline transition-colors duration-200"
              target="_blank"
            >
              <span>{{ $t('landing.footerLinks.inspirationBy') }}</span>
            </a>
          </div>

          <!-- Контакти -->
          <div
            class="col-span-4 sm:col-span-2 lg:col-span-1 flex flex-col gap-3"
          >
            <span
              class="uppercase text-gray-600 font-semibold tracking-wide text-xs mb-2 block"
              >{{ $t('footer.contacts') }}</span
            >
            <a
              href="mailto:capino.app@gmail.com"
              target="_blank"
              class="text-gray-700 hover:text-gray-900 hover:underline transition-colors duration-200"
            >
              capino.app@gmail.com
            </a>
            <span class="text-gray-700 hover:text-gray-900">{{
              $t('footer.companyName')
            }}</span>
            <a
              href="https://maps.app.goo.gl/Q1RB1tF8TdkqD1tk9"
              target="_blank"
              class="text-gray-700 hover:text-gray-900 hover:underline transition-colors duration-200"
            >
              {{ $t('footer.address') }}
            </a>
          </div>
        </div>

        <!-- Нижня частина -->
        <div
          class="flex flex-col-reverse md:flex-row justify-between items-center text-sm text-gray-600 gap-6"
        >
          <div class="flex items-center gap-2">
            <img
              src="/images/capybara-dark-small.png"
              class="w-4 h-4 flex-shrink-0 scale-x-[-1]"
              loading="lazy"
              :alt="$t('footer.capybaraAlt')"
            />
            <span> {{ $t('footer.madeInUkraine') }} </span> <br />
          </div>

          <BaseLanguageSwitch />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import BaseLogo from '@/components/Base/BaseLogo.vue'
import BaseLanguageSwitch from '@/components/Base/BaseLanguageSwitch.vue'

export default {
  name: 'BaseFooter',

  components: {
    BaseLogo,
    BaseLanguageSwitch
  },

  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('locale', ['currentLocale']),

    isLanding() {
      return this.$route?.name === 'home'
    }
  }
}
</script>
